/**
 * Custom Cookie Consent
 * Replaces Shopify's native cookie banner with loopha.com-style design
 */

(function () {
  'use strict';

  const COOKIE_NAME = 'consent';
  const COOKIE_EXPIRY_DAYS = 365;
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [500, 1000, 2000]; // Exponential backoff

  // Track the element that triggered modal open for focus restoration
  let modalTriggerElement = null;

  // Cached DOM references
  let bannerEl = null;
  let modalEl = null;

  // Customer Privacy API loading state
  let customerPrivacyApiReady = false;
  let customerPrivacyApiLoading = false;
  let customerPrivacyCallbacks = [];
  let consentVerificationTimeout = null;

  // Debug mode - enable via URL param or localStorage
  const DEBUG =
    window.location.search.includes('debug_cookie=true') ||
    localStorage.getItem('debug_cookie_consent') === 'true';

  function log(...args) {
    if (DEBUG) console.log('[CookieConsent]', ...args);
  }

  // Minimal debug interface always available (for enable/disable)
  window.__cookieConsentDebug = {
    enable: function () {
      localStorage.setItem('debug_cookie_consent', 'true');
      console.log('[CookieConsent] Debug mode enabled. Reload page to see logs.');
    },
    disable: function () {
      localStorage.removeItem('debug_cookie_consent');
      console.log('[CookieConsent] Debug mode disabled.');
    },
  };

  // Full debug interface only when DEBUG is enabled
  if (DEBUG) {
    Object.assign(window.__cookieConsentDebug, {
      getState: function () {
        return {
          cookie: getCookie(COOKIE_NAME),
          apiReady: customerPrivacyApiReady,
          apiLoading: customerPrivacyApiLoading,
          shopifyConsent: window.Shopify?.customerPrivacy?.currentVisitorConsent?.(),
        };
      },
      verifyConsent: function () {
        const state = this.getState();
        console.log('[CookieConsent] Current state:', state);
        if (state.cookie) {
          console.log('[CookieConsent] Cookie consent level:', state.cookie);
          console.log('[CookieConsent] Expected settings:', CONSENT_SETTINGS[state.cookie]);
        }
        if (state.shopifyConsent) {
          console.log('[CookieConsent] Shopify consent:', state.shopifyConsent);
        } else {
          console.log('[CookieConsent] Shopify consent not available');
        }
        return state;
      },
      // Debug Shopify → Custom Pixel → GTM consent flow
      checkPixelConsent: function () {
        console.log('[CookieConsent] === Shopify Custom Pixel Consent Debug ===');
        console.log('[CookieConsent] GTM is loaded via Shopify Custom Pixel (sandboxed)');
        console.log('[CookieConsent] Consent flows: Banner → Shopify API → Custom Pixel → GTM');
        console.log('');

        // Check Shopify's privacy signal (this is what gets passed to the Custom Pixel)
        const shopifySignal = window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
        console.log('[CookieConsent] Shopify Customer Privacy API state:');
        console.log('  ', shopifySignal);

        if (shopifySignal) {
          // Show how Shopify maps this to the Custom Pixel
          console.log('');
          console.log('[CookieConsent] This translates to Custom Pixel permissions:');
          console.log('  analytics:', shopifySignal.analytics === 'yes' ? '✓ allowed' : '✗ denied');
          console.log('  marketing:', shopifySignal.marketing === 'yes' ? '✓ allowed' : '✗ denied');
          console.log(
            '  preferences:',
            shopifySignal.preferences === 'yes' ? '✓ allowed' : '✗ denied',
          );
          console.log(
            '  sale_of_data:',
            shopifySignal.sale_of_data === 'yes' ? '✓ allowed' : '✗ denied',
          );
        } else {
          console.warn('[CookieConsent] Shopify Customer Privacy API not available');
        }

        console.log('');
        console.log('[CookieConsent] To debug GTM tags inside the Custom Pixel:');
        console.log('  1. Use GTM Preview mode (gtm.google.com → Preview)');
        console.log('  2. Check Network tab for pixel/analytics requests');

        return { shopifySignal: shopifySignal };
      },
      // Watch for consent changes in real-time
      watchConsent: function () {
        console.log('[CookieConsent] Watching for consent changes...');

        // Watch Shopify consent events
        document.addEventListener('visitorConsentCollected', (e) => {
          console.log('[CookieConsent] visitorConsentCollected fired:', e.detail);
          console.log('[CookieConsent] Shopify will now notify Custom Pixel of new consent state');
        });

        console.log(
          '[CookieConsent] Now watching. Interact with the consent banner to see events.',
        );
      },
    });
  }

  /**
   * Setup consent verification listener
   * Listens for Shopify's visitorConsentCollected event to confirm consent was applied
   */
  function setupConsentVerification() {
    document.addEventListener('visitorConsentCollected', (event) => {
      log('visitorConsentCollected event received:', event.detail);
      if (consentVerificationTimeout) {
        clearTimeout(consentVerificationTimeout);
        consentVerificationTimeout = null;
      }
    });
  }

  /**
   * Load Shopify's Customer Privacy API
   * The API must be loaded via loadFeatures before it can be used
   */
  function loadCustomerPrivacyApi(callback, retryCount = 0) {
    // If already ready, call immediately
    if (customerPrivacyApiReady) {
      log('Customer Privacy API already ready');
      if (typeof callback === 'function') callback();
      return;
    }

    // Queue callback
    if (typeof callback === 'function') {
      customerPrivacyCallbacks.push(callback);
    }

    // If already loading, don't load again
    if (customerPrivacyApiLoading) return;
    customerPrivacyApiLoading = true;

    function executeCallbacks() {
      const callbacks = customerPrivacyCallbacks.slice();
      customerPrivacyCallbacks = [];
      callbacks.forEach((cb) => cb());
    }

    function attemptLoad() {
      log(
        'Attempting to load Customer Privacy API (attempt ' +
          (retryCount + 1) +
          '/' +
          MAX_RETRIES +
          ')',
      );

      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (error) {
            console.error('[CookieConsent]', 'loadFeatures error:', error);
            handleRetry();
            return;
          }

          // Verify the API actually loaded by checking for customerPrivacy object
          if (
            window.Shopify &&
            window.Shopify.customerPrivacy &&
            typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function'
          ) {
            customerPrivacyApiReady = true;
            log('Customer Privacy API loaded successfully');
            executeCallbacks();
          } else {
            console.warn(
              '[CookieConsent]',
              'loadFeatures succeeded but customerPrivacy not available',
            );
            handleRetry();
          }
        });
      } else {
        console.warn('[CookieConsent]', 'Shopify.loadFeatures not available');
        // Execute callbacks anyway so UI isn't blocked
        executeCallbacks();
      }
    }

    function handleRetry() {
      if (retryCount < MAX_RETRIES - 1) {
        const delay = RETRY_DELAYS[retryCount] || 2000;
        log('Retrying in ' + delay + 'ms...');
        customerPrivacyApiLoading = false;
        setTimeout(() => loadCustomerPrivacyApi(null, retryCount + 1), delay);
      } else {
        console.error(
          '[CookieConsent]',
          'Failed to load Customer Privacy API after ' + MAX_RETRIES + ' attempts',
        );
        // Execute callbacks anyway so UI isn't blocked
        executeCallbacks();
      }
    }

    attemptLoad();
  }

  /**
   * Cookie helper functions
   */
  function setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();
    document.cookie = name + '=' + value + ';' + expires + ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    const match = document.cookie.split('; ').find((row) => row.startsWith(name + '='));
    return match ? match.split('=')[1] : null;
  }

  /**
   * Get stored consent level
   */
  function getStoredConsentLevel() {
    return getCookie(COOKIE_NAME) || 'marketing';
  }

  /**
   * Consent settings mapped by level
   * Core consent preferences without the headlessStorefront flag (added by getConsentSettings)
   */
  const CONSENT_SETTINGS = {
    marketing: {
      marketing: true,
      analytics: true,
      preferences: true,
      sale_of_data: true,
    },
    functional: {
      marketing: false,
      analytics: true,
      preferences: true,
      sale_of_data: false,
    },
    required: {
      marketing: false,
      analytics: false,
      preferences: false,
      sale_of_data: false,
    },
  };

  /**
   * Map consent level to Shopify's consent categories
   * headlessStorefront: true is required when using a custom cookie banner
   * to tell Shopify to accept our consent values
   */
  function getConsentSettings(level) {
    const settings = CONSENT_SETTINGS[level] || CONSENT_SETTINGS.required;
    return { ...settings, headlessStorefront: true };
  }

  /**
   * Get Shopify's Customer Privacy API if available
   * Returns null if the API is not loaded or setTrackingConsent is not available
   */
  function getCustomerPrivacyApi() {
    const api = window.Shopify?.customerPrivacy;
    return typeof api?.setTrackingConsent === 'function' ? api : null;
  }

  /**
   * Set consent via Shopify's Customer Privacy API
   */
  function setConsent(level, callback) {
    const settings = getConsentSettings(level);
    log('Setting consent level:', level, settings);

    // Store in cookie immediately
    setCookie(COOKIE_NAME, level, COOKIE_EXPIRY_DAYS);

    // Load and use Shopify's Customer Privacy API
    loadCustomerPrivacyApi(() => {
      const api = getCustomerPrivacyApi();
      if (api) {
        // Set a verification timeout to detect if consent wasn't applied
        consentVerificationTimeout = setTimeout(() => {
          console.warn(
            '[CookieConsent]',
            'visitorConsentCollected event not received within 5s - consent may not have been applied',
          );
          if (DEBUG) {
            const current = api.currentVisitorConsent();
            log('Current Shopify consent state:', current);
          }
        }, 5000);

        api.setTrackingConsent(settings, (result) => {
          // Check for errors in the result
          if (result?.error) {
            console.error('[CookieConsent]', 'setTrackingConsent error:', result.error);
          } else {
            log('Consent successfully registered with Shopify');
            // Verify consent was applied in debug mode
            if (DEBUG) {
              const current = api.currentVisitorConsent();
              log('Verified consent state:', current);
            }
          }

          if (typeof callback === 'function') {
            callback();
          }
        });
      } else {
        console.warn('[CookieConsent]', 'Customer Privacy API not available for setConsent');
        // Fallback if API not available
        if (typeof callback === 'function') {
          callback();
        }
      }
    });
  }

  /**
   * Show the cookie banner
   */
  function showBanner() {
    if (bannerEl) {
      // Small delay to allow CSS transition
      setTimeout(() => bannerEl.classList.add('is-active'), 100);
    }
  }

  /**
   * Hide the cookie banner
   */
  function hideBanner() {
    if (bannerEl) {
      bannerEl.classList.remove('is-active');
    }
  }

  /**
   * Lock body scroll (prevents scrolling and compensates for scrollbar width)
   */
  function lockBodyScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = scrollbarWidth + 'px';
    document.body.style.overflow = 'hidden';
  }

  /**
   * Unlock body scroll (restores scrolling)
   */
  function unlockBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  /**
   * Select a radio button by value (handles mutual exclusivity)
   */
  function selectRadio(value) {
    modalEl?.querySelectorAll('.js-cookie-radio').forEach((r) => {
      r.checked = r.value === value;
    });
  }

  /**
   * Show the preferences modal
   */
  function showModal(triggerElement = null) {
    if (!modalEl) return;

    // Store trigger for focus restoration
    modalTriggerElement = triggerElement || document.activeElement;

    lockBodyScroll();
    modalEl.classList.add('show');

    // Set the current consent level in the modal
    const currentLevel = getStoredConsentLevel();
    selectRadio(currentLevel);

    // Trap focus and focus first interactive element
    const modalContent = modalEl.querySelector('.cookie-consent-modal__content');
    const firstRadio = modalEl.querySelector('.js-cookie-radio');
    if (typeof trapFocus === 'function') {
      trapFocus(modalEl, firstRadio || modalContent);
    } else if (firstRadio) {
      firstRadio.focus();
    }
  }

  /**
   * Hide the preferences modal
   */
  function hideModal() {
    if (!modalEl) return;

    modalEl.classList.remove('show');
    unlockBodyScroll();

    // Remove focus trap and restore focus to trigger element
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(modalTriggerElement);
    } else if (modalTriggerElement) {
      modalTriggerElement.focus();
    }
    modalTriggerElement = null;
  }

  /**
   * Click handler functions mapped by class name
   */
  function handleAccept(e) {
    const level = e.target.dataset.consent || 'marketing';
    setConsent(level, () => {
      hideBanner();
      if (level === 'marketing') window.location.reload();
    });
  }

  function handleClose() {
    setConsent('marketing', () => {
      hideBanner();
      window.location.reload();
    });
  }

  function handleOpenSettings(e) {
    showModal(e.target);
  }

  function handleSave() {
    if (!modalEl) return;
    const selectedRadio = modalEl.querySelector('.js-cookie-radio:checked');
    const level = selectedRadio ? selectedRadio.value : 'required';
    setConsent(level, () => {
      hideModal();
      hideBanner();
      if (level === 'marketing') window.location.reload();
    });
  }

  /**
   * Handler map for delegated click events
   */
  const clickHandlers = {
    'js-cookie_consent-btn': handleAccept,
    'js-cookie_close-btn': handleClose,
    'js-cookie_settings-btn': handleOpenSettings,
    'js-modal-close': hideModal,
    'js-cookie_save-btn': handleSave,
  };

  /**
   * Initialize event listeners
   */
  function initEventListeners() {
    // Single delegated click handler
    document.addEventListener('click', (e) => {
      // Check handler map for class-based delegation
      for (const className in clickHandlers) {
        if (e.target.classList.contains(className)) {
          e.preventDefault();
          clickHandlers[className](e);
          return;
        }
      }

      // Handle settings-item click (radio label click)
      const item = e.target.closest('.cookie-consent-modal__settings-item');
      if (item && !e.target.matches('input')) {
        const radio = item.querySelector('input[type="radio"]');
        if (radio) {
          selectRadio(radio.value);
        }
        return;
      }

      // Handle click outside modal content to close
      if (modalEl && modalEl.classList.contains('show')) {
        const modalContent = modalEl.querySelector('.cookie-consent-modal__content');
        if (modalEl.contains(e.target) && !modalContent.contains(e.target)) {
          hideModal();
        }
      }
    });

    // Single delegated keydown handler
    document.addEventListener('keydown', (e) => {
      // Escape key closes modal
      if (e.key === 'Escape') {
        hideModal();
        return;
      }

      // Space/Enter on settings link opens modal
      if (e.target.classList.contains('js-cookie_settings-btn')) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          showModal(e.target);
        }
      }
    });

    // Handle mutual exclusivity for cookie radios
    document.addEventListener('change', function (e) {
      if (e.target.classList.contains('js-cookie-radio')) {
        selectRadio(e.target.value);
      }
    });
  }

  /**
   * Initialize DOM element references
   * Elements are rendered server-side via Liquid snippets
   */
  function initDomReferences() {
    bannerEl = document.getElementById('customCookieBanner');
    modalEl = document.getElementById('cookieConsentModal');

    if (!bannerEl) {
      console.warn(
        '[CookieConsent]',
        'Cookie banner element not found - ensure cookie-banner.liquid snippet is rendered',
      );
    }
    if (!modalEl) {
      console.warn(
        '[CookieConsent]',
        'Cookie modal element not found - ensure cookie-modal.liquid snippet is rendered',
      );
    }
  }

  /**
   * Sync consent state with Shopify's Customer Privacy API
   * If consent cookie exists: restore that consent level to Shopify
   * If no consent cookie: set denied state to block tracking until user chooses
   */
  function syncConsentWithShopify() {
    const existingConsent = getCookie(COOKIE_NAME);

    // Load Shopify's Customer Privacy API
    loadCustomerPrivacyApi(() => {
      const api = getCustomerPrivacyApi();
      if (!api) {
        console.warn('[CookieConsent]', 'Customer Privacy API not available');
        return;
      }

      const level = existingConsent || 'required';
      const settings = getConsentSettings(level);
      const isRestoring = existingConsent !== null;
      const actionMsg = isRestoring ? 'Restoring consent' : 'Setting initial denied state';
      const successMsg = isRestoring
        ? 'Consent restored successfully'
        : 'Initial denied state set successfully - tracking is blocked';

      log(`${actionMsg}:`, settings);
      api.setTrackingConsent(settings, (result) => {
        if (result?.error) {
          console.error('[CookieConsent]', `Failed: ${actionMsg.toLowerCase()}:`, result.error);
        } else {
          log(successMsg);
        }
      });
    });
  }

  /**
   * Determine if the cookie banner should be shown
   * Checks our cookie first (reliable, synchronous), then consults Shopify API
   */
  function shouldShowBanner() {
    const consentCookie = getCookie(COOKIE_NAME);

    // User has already given consent via our banner
    if (consentCookie !== null) {
      log('Consent cookie found:', consentCookie, '- not showing banner');
      return false;
    }

    // No cookie, check Shopify's recommendation if available
    const api = window.Shopify?.customerPrivacy;
    if (typeof api?.shouldShowBanner === 'function') {
      const result = api.shouldShowBanner();
      log('No consent cookie, Shopify shouldShowBanner():', result);
      return result;
    }

    // No cookie and no Shopify API - show the banner
    log('No consent cookie and no Shopify API - showing banner');
    return true;
  }

  /**
   * Initialize design mode (Shopify theme editor).
   * Shows/hides banner via editor events, skips consent API and page reloads.
   */
  function initDesignMode() {
    // Override handlers to skip consent API, cookie writes, and page reloads
    clickHandlers['js-cookie_consent-btn'] = function () {
      hideBanner();
    };
    clickHandlers['js-cookie_close-btn'] = function () {
      hideBanner();
    };
    clickHandlers['js-cookie_save-btn'] = function () {
      hideModal();
      hideBanner();
    };

    // Bind UI events (uses overridden handlers)
    initEventListeners();

    // Show banner when section is selected in editor sidebar
    document.addEventListener('shopify:section:select', function (event) {
      if (!event.target.querySelector('#customCookieBanner')) return;
      showBanner();
    });

    // Hide banner (and modal) when section is deselected
    document.addEventListener('shopify:section:deselect', function (event) {
      if (!event.target.querySelector('#customCookieBanner')) return;
      hideBanner();
      hideModal();
    });

    // Re-cache DOM references when editor re-renders the section
    document.addEventListener('shopify:section:load', function (event) {
      if (!event.target.querySelector('#customCookieBanner')) return;
      initDomReferences();
    });
  }

  /**
   * Initialize custom cookie consent
   */
  function initCustomCookieConsent() {
    log('Initializing custom cookie consent');

    // Cache DOM references (elements are rendered server-side via Liquid)
    initDomReferences();

    // Design mode: preview-only UI, no consent API or cookies
    if (Shopify.designMode) {
      initDesignMode();
      return;
    }

    // Setup consent verification listener early
    setupConsentVerification();

    // Initialize event listeners
    initEventListeners();

    // Sync consent with Shopify's Customer Privacy API
    syncConsentWithShopify();

    // Show banner if no existing consent
    if (shouldShowBanner()) {
      showBanner();
    }
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomCookieConsent);
  } else {
    initCustomCookieConsent();
  }
})();
